import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, API_BASE } from '@/lib/api';
import { CHART_INITIAL_DIMENSIONS } from '@/constants/providers';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartCard, EmptyState } from '@/components/ChartCard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, LineChart, Line, CartesianGrid,
} from 'recharts';
import {
  Sparkles, Loader2, ArrowUpRight, ArrowDownRight, FileText, Table2, Info, ChevronLeft,
  Link2, Copy, Check, Eye,
} from 'lucide-react';

// -----------------------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------------------
const fmt = (n) => (n == null ? '—' : Number(n).toFixed(0));
const priorityColor = (p) =>
  p === 'high'
    ? 'border-rose-500/40 text-rose-700 bg-rose-500/10'
    : p === 'medium'
      ? 'border-amber-500/40 text-amber-700 bg-amber-500/10'
      : 'text-muted-foreground';

// Format an ISO timestamp as "just now / 2h ago / 3d ago / Mar 4"
function relativeTime(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (diffSec < 45) return 'just now';
    const min = Math.round(diffSec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.round(hr / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_e) { return null; }
}

// Small pill shown next to shared reports summarising view analytics
function ShareAnalyticsPill({ report, testId }) {
  if (!report?.share_token) return null;
  const count = Number(report.share_view_count || 0);
  const last = relativeTime(report.share_last_viewed_at);
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-800 px-2 py-0.5 text-[11px]"
      title={report.share_last_viewed_at ? `Last opened ${new Date(report.share_last_viewed_at).toLocaleString()}` : 'No views yet'}
      data-testid={testId}
    >
      <Eye className="h-3 w-3" />
      {count === 0
        ? 'No views yet'
        : `${count.toLocaleString()} view${count === 1 ? '' : 's'}${last ? ` · ${last}` : ''}`}
    </div>
  );
}

function SubScoreCard({ label, value, hint, testId }) {
  return (
    <Card className="border p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
      <div className="mt-1 text-3xl font-display font-semibold tabular">{fmt(value)}</div>
      <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{hint}</div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Share toggle (Growth+)
// -----------------------------------------------------------------------------
function ShareToggle({ report, onChange }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasToken = !!report.share_token;
  const publicUrl = hasToken ? `${window.location.origin}/share/${report.share_token}` : '';

  const enable = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/audit-reports/${report.id}/share`);
      onChange({ share_token: r.data.share_token });
      toast.success('Share link enabled');
    } catch (_e) { /* 402 handled globally */ }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/audit-reports/${report.id}/share`);
      onChange({ share_token: null });
      toast.success('Share link revoked');
    } finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* clipboard may be blocked */ }
  };

  if (!hasToken) {
    return (
      <Button size="sm" variant="outline" onClick={enable} disabled={busy} data-testid="audit-share-enable">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />}
        Share
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1.5 rounded-lg border bg-secondary/40 pl-2 pr-1 py-1" data-testid="audit-share-active">
      <Link2 className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
      <span className="text-xs font-mono max-w-[220px] truncate" title={publicUrl}>{publicUrl.replace(/^https?:\/\//, '')}</span>
      <Button size="sm" variant="ghost" onClick={copy} className="h-7 px-2" data-testid="audit-share-copy">
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
      <Button size="sm" variant="ghost" onClick={revoke} disabled={busy} className="h-7 px-2 text-rose-600 hover:text-rose-700" data-testid="audit-share-revoke">
        Revoke
      </Button>
    </div>
  );
}

function authedDownload(url, filename) {
  const token = localStorage.getItem('ageorush_token');
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail?.detail?.message || detail?.detail || `Export failed (${r.status})`);
      }
      const blob = await r.blob();
      const dl = document.createElement('a');
      dl.href = URL.createObjectURL(blob);
      dl.download = filename;
      dl.click();
      URL.revokeObjectURL(dl.href);
    })
    .catch((e) => toast.error(e.message || 'Export failed'));
}

// -----------------------------------------------------------------------------
// Main tab
// -----------------------------------------------------------------------------
export default function AuditReportTab({ project }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${project.id}/audit-reports`);
      setReports(r.data.reports || []);
    } catch (_e) {
      toast.error('Could not load reports. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setSelected(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await api.get(`/audit-reports/${id}`);
      setDetail(r.data);
    } catch (_e) {
      toast.error('Could not load report');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/projects/${project.id}/audit-reports`);
      toast.success('Audit report generated');
      await load();
      openDetail(r.data.id);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (typeof d === 'string') toast.error(d);
      // 402s are already handled by the global interceptor
    } finally {
      setGenerating(false);
    }
  };

  // ----- Detail view -----
  if (selected) {
    return (
      <div className="space-y-4" data-testid="audit-detail-view">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setDetail(null); }} data-testid="audit-back-button">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to reports
          </Button>
          {detail && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {detail.share_token && (
                <ShareAnalyticsPill report={detail} testId="audit-detail-share-analytics" />
              )}
              <ShareToggle report={detail} onChange={(u) => setDetail((d) => ({ ...d, ...u }))} />
              <Button size="sm" variant="outline" onClick={() => authedDownload(`${API_BASE}/audit-reports/${detail.id}/export.csv`, `audit-${detail.id.slice(0,8)}.csv`)} data-testid="audit-export-csv">
                <Table2 className="h-4 w-4 mr-1.5" /> CSV
              </Button>
              <Button size="sm" onClick={() => authedDownload(`${API_BASE}/audit-reports/${detail.id}/export.pdf`, `audit-${detail.id.slice(0,8)}.pdf`)} data-testid="audit-export-pdf">
                <FileText className="h-4 w-4 mr-1.5" /> PDF
              </Button>
            </div>
          )}
        </div>

        {detailLoading || !detail ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-32" />)}</div>
        ) : (
          <ReportDetail detail={detail} project={project} />
        )}
      </div>
    );
  }

  // ----- List view -----
  return (
    <div className="space-y-4" data-testid="audit-list-view">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-lg font-semibold">AI Visibility Audit</div>
          <div className="text-sm text-muted-foreground">Snapshot of how AI answer engines cover {project.name}.</div>
        </div>
        <Button onClick={generate} disabled={generating} data-testid="audit-generate-button">
          {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          Generate report
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : reports.length === 0 ? (
        <ChartCard title="No audit reports yet">
          <EmptyState
            title="Generate your first audit"
            description="We'll analyse your latest scan and score your AI visibility across mention rate, citations, share of voice, and sentiment."
          />
        </ChartCard>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => (
            <button
              key={r.id}
              onClick={() => openDetail(r.id)}
              className="w-full text-left flex items-center justify-between rounded-lg border p-3 hover:bg-secondary/40 transition-colors"
              data-testid={`audit-list-row-${r.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="text-2xl font-display font-semibold text-[hsl(var(--accent))] tabular w-14 text-center">{fmt(r.visibility_score)}</div>
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    Visibility audit
                    {r.share_token && (
                      <ShareAnalyticsPill report={r} testId={`audit-list-share-analytics-${r.id}`} />
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">Generated {(r.generated_at || '').split('T')[0]} · {r.total_results} answers</div>
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Report Detail
// -----------------------------------------------------------------------------
function ReportDetail({ detail, project }) {
  const sub = detail.sub_scores || {};
  const provs = detail.provider_breakdown || {};
  const delta = detail.delta_vs_previous;
  const genDate = (detail.generated_at || '').split('T')[0];
  const activeProviders = detail.active_providers || [];
  const totalPrompts = new Set((detail.prompt_table || []).map((p) => p.prompt_id)).size;

  const sovData = useMemo(() => {
    const brandName = project.name || 'Brand';
    return Object.entries(detail.competitor_sov || {}).map(([name, count]) => ({
      name, count, isBrand: name === brandName,
    }));
  }, [detail.competitor_sov, project.name]);

  const trendData = useMemo(
    () => (detail.trend || []).map((t) => ({
      date: (t.generated_at || '').split('T')[0].slice(5),
      score: t.visibility_score,
    })),
    [detail.trend],
  );

  const weights = detail.weights || {};

  return (
    <>
      {/* Headline */}
      <Card className="border p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Visibility score</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="text-5xl font-display font-semibold text-[hsl(var(--accent))] tabular" data-testid="audit-headline-score">
                {fmt(detail.visibility_score)}
              </div>
              <div className="text-lg text-muted-foreground">/ 100</div>
              {delta != null && (
                <Badge
                  variant="outline"
                  className={`text-[11px] gap-1 ${delta >= 0 ? 'border-emerald-500/40 text-emerald-700 bg-emerald-500/10' : 'border-rose-500/40 text-rose-700 bg-rose-500/10'}`}
                  data-testid="audit-delta-badge"
                >
                  {delta >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {delta >= 0 ? '+' : ''}{delta} vs last report
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-2 max-w-2xl">
              This report is a snapshot generated on <span className="font-medium text-foreground">{genDate}</span> from a scan of <span className="font-medium text-foreground">{totalPrompts} prompts</span> across <span className="font-medium text-foreground">{activeProviders.join(', ') || '—'}</span>. Scores can shift slightly between runs — AI responses aren't perfectly deterministic.
            </div>
          </div>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="audit-how-calculated">
                  <Info className="h-3 w-3" /> How this is calculated
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs text-xs leading-relaxed">
                <div className="font-medium mb-1">Visibility score formula</div>
                <div className="opacity-80">
                  {Math.round((weights.mention_rate || 0.35) * 100)}% Mention · {Math.round((weights.citation_strength || 0.30) * 100)}% Citation · {Math.round((weights.share_of_voice || 0.20) * 100)}% SoV · {Math.round((weights.sentiment || 0.15) * 100)}% Sentiment
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </Card>

      {/* Sub-scores */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <SubScoreCard testId="audit-sub-mention" label="Mention Rate" value={sub.mention_rate} hint="How often you appear in AI answers" />
        <SubScoreCard testId="audit-sub-citation" label="Citation Strength" value={sub.citation_strength} hint="How many mentions come with a source URL" />
        <SubScoreCard testId="audit-sub-sov" label="Share of Voice" value={sub.share_of_voice} hint="Your slice vs tracked competitors" />
        <SubScoreCard testId="audit-sub-sentiment" label="Sentiment" value={sub.sentiment} hint="Average tone when your brand comes up" />
      </div>

      {/* Provider breakdown */}
      <ChartCard title="Provider breakdown" subtitle="Same four sub-scores, split per AI provider">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-provider-table">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr className="border-b">
                <th className="text-left py-2 font-medium">Provider</th>
                <th className="text-right py-2 font-medium">Score</th>
                <th className="text-right py-2 font-medium">Mention</th>
                <th className="text-right py-2 font-medium">Citation</th>
                <th className="text-right py-2 font-medium">SoV</th>
                <th className="text-right py-2 font-medium">Sentiment</th>
                <th className="text-right py-2 font-medium">Runs</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(provs).map(([p, v]) => (
                <tr key={p} className="border-b last:border-0">
                  <td className="py-2 capitalize">{p}</td>
                  <td className="text-right tabular font-semibold text-[hsl(var(--accent))]">{fmt(v.visibility_score)}</td>
                  <td className="text-right tabular">{fmt(v.mention_rate)}</td>
                  <td className="text-right tabular">{fmt(v.citation_strength)}</td>
                  <td className="text-right tabular">{fmt(v.share_of_voice)}</td>
                  <td className="text-right tabular">{fmt(v.sentiment)}</td>
                  <td className="text-right tabular text-muted-foreground">{v.runs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Two-column: SoV bar + trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Share of voice" subtitle="Brand vs tracked competitors">
          <div className="h-64" data-testid="audit-sov-chart">
            <ResponsiveContainer initialDimension={CHART_INITIAL_DIMENSIONS}>
              <BarChart data={sovData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="name" fontSize={11} />
                <YAxis fontSize={11} />
                <RTooltip cursor={{ fill: 'rgba(19,154,173,0.08)' }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {sovData.map((d, i) => (
                    <Bar key={i} fill={d.isBrand ? 'hsl(var(--accent))' : '#94a3b8'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Score trend" subtitle={trendData.length >= 2 ? 'Visibility score over time' : 'Generate more reports to see a trend'}>
          <div className="h-64" data-testid="audit-trend-chart">
            {trendData.length >= 2 ? (
              <ResponsiveContainer initialDimension={CHART_INITIAL_DIMENSIONS}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} domain={[0, 100]} />
                  <RTooltip />
                  <Line type="monotone" dataKey="score" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                One report so far. Run another to see how you're trending.
              </div>
            )}
          </div>
        </ChartCard>
      </div>

      {/* Prompt-level audit table */}
      <ChartCard title="Prompt-level audit" subtitle="One row per prompt · shows mentioned/cited/sentiment per provider">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="audit-prompt-table">
            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr className="border-b">
                <th className="text-left py-2 font-medium">Prompt</th>
                {activeProviders.map((p) => (
                  <th key={p} className="text-center py-2 font-medium capitalize">{p}</th>
                ))}
                <th className="text-left py-2 font-medium">Competitors seen</th>
              </tr>
            </thead>
            <tbody>
              {(detail.prompt_table || []).map((row) => (
                <tr key={row.prompt_id} className="border-b last:border-0 align-top">
                  <td className="py-2 pr-3 max-w-xs">
                    <div className="truncate">{row.prompt_text}</div>
                  </td>
                  {activeProviders.map((p) => {
                    const cell = (row.providers || {})[p];
                    if (!cell) return <td key={p} className="text-center text-muted-foreground">—</td>;
                    return (
                      <td key={p} className="text-center py-2">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className={cell.mentioned ? 'text-emerald-700' : 'text-rose-700'}>{cell.mentioned ? '✓' : '✕'}</span>
                          {cell.cited && cell.citation_url && (
                            <a href={cell.citation_url} target="_blank" rel="noreferrer" className="text-[10px] text-[hsl(var(--accent))] hover:underline truncate max-w-[100px]" title={cell.citation_url}>cite</a>
                          )}
                          {cell.sentiment != null && (
                            <span className="text-[10px] text-muted-foreground tabular">{Number(cell.sentiment).toFixed(2)}</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {(row.competitors || []).map((c) => (
                        <Badge key={c} variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 bg-amber-500/10">{c}</Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Recommendations + Cited domains */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Recommendations" subtitle={`${(detail.recommendations || []).length} suggestions, ranked by priority`}>
          <ol className="space-y-3" data-testid="audit-recommendations">
            {(detail.recommendations || []).length === 0 ? (
              <li className="text-sm text-muted-foreground text-center py-6">No recommendations — you're in great shape!</li>
            ) : (detail.recommendations || []).map((rec, i) => (
              <li key={i} className="flex items-start gap-2">
                <Badge variant="outline" className={`text-[10px] shrink-0 mt-0.5 ${priorityColor(rec.priority)}`}>{rec.priority}</Badge>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{rec.title}</div>
                  <div className="text-xs text-muted-foreground">{rec.description}</div>
                </div>
              </li>
            ))}
          </ol>
        </ChartCard>

        <ChartCard title="Most cited domains" subtitle="Every domain cited across all prompts + providers">
          <div className="space-y-1 max-h-[400px] overflow-y-auto" data-testid="audit-cited-domains">
            {(detail.cited_domains || []).length === 0 ? (
              <div className="text-xs text-muted-foreground py-6 text-center">No citations captured in this scan.</div>
            ) : (detail.cited_domains || []).slice(0, 30).map((d, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border px-3 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${d.owner === 'brand' ? 'border-emerald-500/40 text-emerald-700 bg-emerald-500/10' : d.owner === 'third_party' || !d.owner ? 'text-muted-foreground' : 'border-amber-500/40 text-amber-700 bg-amber-500/10'}`}>
                    {d.owner === 'brand' ? 'Brand' : (d.owner === 'third_party' || !d.owner) ? '3rd party' : d.owner}
                  </Badge>
                  <span className="text-xs truncate">{d.domain}</span>
                </div>
                <span className="text-xs tabular font-medium">{d.count}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </>
  );
}
